import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  MpesaC2bUrlsState,
  MpesaMode,
  MpesaProfileStatus,
} from '../../utils/mpesaActivation';

export interface BackofficeMpesaConfigDraft {
  bp_mpesa_c2b_consumer_key: string;
  bp_mpesa_c2b_consumer_secret: string;
  bp_mpesa_b2c_consumer_key: string;
  bp_mpesa_b2c_consumer_secret: string;
  bp_mpesa_b2b_consumer_key: string;
  bp_mpesa_b2b_consumer_secret: string;
  bp_mpesa_passkey: string;
  bp_mpesa_c2b_shortcode: string;
  bp_mpesa_c2b_shortcode_type: string;
  /** Till only: the Store/Head Office number the Till went live under. */
  bp_mpesa_stk_business_shortcode: string;
  /** Till only: the Till number (PartyB); same as the C2B shortcode. */
  bp_mpesa_till_number: string;
  bp_mpesa_b2c_shortcode: string;
  bp_mpesa_b2b_shortcode: string;
  bp_mpesa_b2c_initiator_name: string;
  bp_mpesa_b2b_initiator_name: string;
  bp_mpesa_security_credential: string;
  bp_mpesa_b2c_initiator_password: string;
}

export interface BackofficeMpesaConfigFetched {
  _id?: string;
  bp_location_id?: string;
  bp_mpesa_configuration_c2b_shortcode_information?: string;
  bp_mpesa_configuration_c2b_shortcode_type_information?: string;
  bp_mpesa_configuration_stk_business_shortcode_information?: string | null;
  bp_mpesa_configuration_till_number_information?: string | null;
  bp_mpesa_configuration_b2c_shortcode_information?: string;
  bp_mpesa_configuration_b2b_shortcode_information?: string;
  bp_mode?: MpesaMode | null;
  bp_status?: MpesaProfileStatus;
  bp_c2b_urls_state?: MpesaC2bUrlsState | null;
  bp_mpesa_configuration_information_created_at?: string;
  bp_mpesa_configuration_information_updated_at?: string;
}

/** Keys copied from the owner GET/PATCH response into the persisted slice. */
export const BACKOFFICE_MPESA_CONFIG_FETCHED_KEYS: ReadonlyArray<
  keyof BackofficeMpesaConfigFetched
> = [
  '_id',
  'bp_location_id',
  'bp_mpesa_configuration_c2b_shortcode_information',
  'bp_mpesa_configuration_c2b_shortcode_type_information',
  'bp_mpesa_configuration_stk_business_shortcode_information',
  'bp_mpesa_configuration_till_number_information',
  'bp_mpesa_configuration_b2c_shortcode_information',
  'bp_mpesa_configuration_b2b_shortcode_information',
  'bp_mode',
  'bp_status',
  'bp_c2b_urls_state',
  'bp_mpesa_configuration_information_created_at',
  'bp_mpesa_configuration_information_updated_at',
];

/** Picks only the safe, known keys off a config response (never secrets). */
export const pickBackofficeMpesaConfigFetched = (
  data: Record<string, unknown> | null | undefined,
): BackofficeMpesaConfigFetched | null => {
  if (!data || typeof data !== 'object') return null;
  return BACKOFFICE_MPESA_CONFIG_FETCHED_KEYS.reduce((acc, key) => {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      (acc as Record<string, unknown>)[key] = data[key];
    }
    return acc;
  }, {} as BackofficeMpesaConfigFetched);
};

export interface BackofficeMpesaConfigSliceObject {
  draft: BackofficeMpesaConfigDraft;
  fetched: BackofficeMpesaConfigFetched | null;
}

const initialDraft: BackofficeMpesaConfigDraft = {
  bp_mpesa_c2b_consumer_key: '',
  bp_mpesa_c2b_consumer_secret: '',
  bp_mpesa_b2c_consumer_key: '',
  bp_mpesa_b2c_consumer_secret: '',
  bp_mpesa_b2b_consumer_key: '',
  bp_mpesa_b2b_consumer_secret: '',
  bp_mpesa_passkey: '',
  bp_mpesa_c2b_shortcode: '',
  bp_mpesa_c2b_shortcode_type: '',
  bp_mpesa_stk_business_shortcode: '',
  bp_mpesa_till_number: '',
  bp_mpesa_b2c_shortcode: '',
  bp_mpesa_b2b_shortcode: '',
  bp_mpesa_b2c_initiator_name: '',
  bp_mpesa_b2b_initiator_name: '',
  bp_mpesa_security_credential: '',
  bp_mpesa_b2c_initiator_password: '',
};

const initialState: BackofficeMpesaConfigSliceObject = {
  draft: initialDraft,
  fetched: null,
};

/** The Connect form's Picker uses "Paybill" / "Till"; the API stores lowercase. */
const normalizeShortcodeTypeForPicker = (
  value: string | null | undefined,
): string => {
  const lower = String(value || '')
    .trim()
    .toLowerCase();
  if (lower === 'paybill') return 'Paybill';
  if (lower === 'till') return 'Till';
  return '';
};

const mpesaConfigSlice = createSlice({
  name: 'mpesaConfig',
  initialState,
  reducers: {
    setMpesaDraftField: (
      state,
      action: PayloadAction<{
        name: keyof BackofficeMpesaConfigDraft;
        value: string;
      }>,
    ) => {
      const { name, value } = action.payload;
      state.draft[name] = value;
    },
    setMpesaDraft: (
      state,
      action: PayloadAction<Partial<BackofficeMpesaConfigDraft>>,
    ) => {
      state.draft = { ...state.draft, ...(action.payload || {}) };
    },
    resetMpesaDraft: state => {
      state.draft = initialDraft;
    },
    /**
     * Edit mode: prefill the non-secret fields from the saved config so the
     * owner only types what changes. Secrets stay blank ("" = keep current).
     */
    setMpesaDraftFromFetched: (
      state,
      action: PayloadAction<BackofficeMpesaConfigFetched | null>,
    ) => {
      const fetched = action.payload;
      state.draft = {
        ...initialDraft,
        bp_mpesa_c2b_shortcode:
          fetched?.bp_mpesa_configuration_c2b_shortcode_information ?? '',
        bp_mpesa_c2b_shortcode_type: normalizeShortcodeTypeForPicker(
          fetched?.bp_mpesa_configuration_c2b_shortcode_type_information,
        ),
        bp_mpesa_stk_business_shortcode:
          fetched?.bp_mpesa_configuration_stk_business_shortcode_information ??
          '',
        bp_mpesa_till_number:
          fetched?.bp_mpesa_configuration_till_number_information ?? '',
        bp_mpesa_b2c_shortcode:
          fetched?.bp_mpesa_configuration_b2c_shortcode_information ?? '',
        bp_mpesa_b2b_shortcode:
          fetched?.bp_mpesa_configuration_b2b_shortcode_information ?? '',
      };
    },
    setFetchedMpesaConfig: (
      state,
      action: PayloadAction<BackofficeMpesaConfigFetched | null>,
    ) => {
      state.fetched = action.payload ?? null;
    },
    clearFetchedMpesaConfig: state => {
      state.fetched = null;
    },
  },
});

export const {
  setMpesaDraftField,
  setMpesaDraft,
  resetMpesaDraft,
  setMpesaDraftFromFetched,
  setFetchedMpesaConfig,
  clearFetchedMpesaConfig,
} = mpesaConfigSlice.actions;

export default mpesaConfigSlice.reducer;

// Selectors
import type { RootState } from '../types';

export const selectBackofficeMpesaConfigDraft = (state: RootState) =>
  state.backofficeMpesaConfig.draft;

export const selectFetchedMpesaConfig = (state: RootState) =>
  state.backofficeMpesaConfig.fetched;
